import { gameConverter, gameToDoc } from 'api/converters/GameConverter';
import { playerConverter, playerToDoc } from 'api/converters/PlayerConverter';
import { waitForUserToLogIn } from 'api/UserAPI';
import { ActionCls, ArmyGreenCls, CrimsonCls, Emojis, InfoCls, LocationCls, ValueCls } from 'constants/constants';
import { SqRoutes } from 'constants/Routes';
import {
  and,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  Unsubscribe,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { generatePicksList, getGameUrl, getInitialSqPlayer, initialSquareGame } from 'helpers/ArrayHelpers';
import { redirect, LoaderFunctionArgs, ActionFunctionArgs } from 'react-router-dom';
import {
  getActiveSqGame,
  setActiveGameOpenSquares,
  setActiveGamePicks,
  setActiveGamePlayers,
  setActiveSqGame,
} from 'slices/ActiveGameSlice';
import { getEnhancedMatches } from 'slices/SqScheduleSlice';
import {
  getAllSqGames,
  setActiveSqGameId,
  setActiveSqPlayerId,
  setCompletedGames,
  setGames,
  setResumeGames,
} from 'slices/SquaresSlice';
import { getFbSqUser } from 'slices/SqUserSlice';
import { store } from 'slices/store';
import { Maybe } from 'types/App.types';
import {
  FbSqUser,
  SqGame,
  SqGameBackEndModel,
  SqGameID,
  SqGamePayloadAction,
  SqGamePayloadActionType,
  SqPlayer,
  SqPlayerMarker,
  SqTeam,
  SqUserID,
} from 'types/SqGame.types';
import { auth, db } from '../firebase';

export let unsubscribeFromActiveGame: Unsubscribe;
export let unsubscribeFromPlayersInActiveGame: Unsubscribe;

export let unsubscribeFromLobbyJoinGameUpdates: Unsubscribe;
export let unsubscribeFromLobbyCompletedGamesUpdates: Unsubscribe;
export let unsubscribeToLobbyResumeGamesUpdates: Unsubscribe;

// #region FETCH GAMES/GAME/PLAYERS *******************************************

const setAdminPosition = (game: SqGame, players: SqPlayer[]) => {
  const locPlayers = players.slice();
  const adminIndex = locPlayers.findIndex((p: SqPlayer) => p.id === game.gameAdmin.id);
  if (adminIndex > 0) {
    const tempAdminRec = locPlayers[adminIndex];
    locPlayers.splice(adminIndex, 1);
    locPlayers.unshift(tempAdminRec);
  }
  return locPlayers;
};

const updateActiveGameProps = (game: Maybe<SqGame>, players: SqPlayer[] = []): Maybe<SqGame> => {
  if (game && players.length > 0) {
    console.log(`${Emojis.eyes} Active Game Players count: %c ${players.length} %c`, ValueCls, '');
    const playerIds = players.map((p) => p.id);
    const adminIndex = playerIds.indexOf(game.gameAdmin.id);
    if (adminIndex > 0) {
      playerIds.splice(adminIndex, 1);
      playerIds.unshift(game.gameAdmin.id);
    }
    const openSquares = 100 - players.reduce((sum, p) => sum + p.numberOfPicks, 0);
    const picks = generatePicksList(players);
    const pl = setAdminPosition(game, players);

    store.dispatch(setActiveGameOpenSquares(openSquares));
    store.dispatch(setActiveGamePicks(picks));
    store.dispatch(setActiveGamePlayers(pl));

    return { ...game, picks, players: pl, playerIds, openSquares };
  }

  return;
};

/**
 * Observable GET call for available games to show in the `Lobby'.
 *
 * @description On every change from active to completed to games in `games` collection in FB,
 * observer will be notified and the games will appear in `Completed` section of the lobby and
 * disappear from the `Resume` section of the Lobby scene.
 */
export const subscribeToLobbyCompletedGamesUpdates = async (playerId: string): Promise<void> => {
  const collectionData = collection(db, 'games').withConverter(gameConverter);
  const q = query(
    collectionData,
    and(where('playerIds', 'array-contains-any', [playerId]), where('gameProgress', '==', 6))
    // orderBy('createdOn', 'desc')
  ); //, limitToLast(5));

  unsubscribeFromLobbyCompletedGamesUpdates = onSnapshot(q, (querySnapshot) => {
    const games: SqGameBackEndModel[] = [];
    querySnapshot.docs.forEach((doc) => {
      const docData: SqGameBackEndModel = doc.data();
      games.push({
        ...docData,
        sqGameId: doc.id,
      });
    });
    store.dispatch(setCompletedGames(games));
  });
};

/**
 * Observable GET call for available games to show in the `Lobby'.
 *
 * @description On every change to games that have been created by or joined by the current user
 * until these games have been completed, the games will be stored in the `Resume` section of the Lobby,
 * and this observer is responsible for keeping them synched with the FB.
 */
export const subscribeToLobbyResumeGamesUpdates = async (playerId: string): Promise<void> => {
  const collectionData = collection(db, 'games').withConverter(gameConverter);
  const q = query(
    collectionData,
    and(where('playerIds', 'array-contains-any', [playerId]), where('gameProgress', '<', 6))
    // orderBy('createdOn', 'desc')
  ); //, limitToLast(5));

  unsubscribeToLobbyResumeGamesUpdates = onSnapshot(q, (querySnapshot) => {
    const games: SqGameBackEndModel[] = [];
    querySnapshot.docs.forEach((doc) => {
      const docData: SqGameBackEndModel = doc.data();
      games.push({
        ...docData,
        sqGameId: doc.id,
      });
    });
    store.dispatch(setResumeGames(games));
  });
};

/**
 * Observable GET call for available games to show in the `Lobby'.
 *
 * @description On every change to `games` collection to the games in the app that current user
 * did not create or join, such as another user creates a game, an open game, a reopened game,
 * or the game is no longer available to due being full, or already started the observer will
 * update the state of those games. Games will be accessible from the `Join` section of the Lobby.
 */
export const subscribeToLobbyJoinGameUpdates = async (playerId: string): Promise<void> => {
  const collectionData = collection(db, 'games').withConverter(gameConverter);
  const q = query(
    collectionData,
    and(where('playerIds', 'not-in', [playerId]), where('gameProgress', '==', 0))
    // orderBy('createdOn', 'desc')
  ); //, limitToLast(5));

  unsubscribeFromLobbyJoinGameUpdates = onSnapshot(q, (querySnapshot) => {
    const games: SqGameBackEndModel[] = [];
    querySnapshot.docs.forEach((doc) => {
      const docData: SqGameBackEndModel = doc.data();
      games.push({
        ...docData,
        sqGameId: doc.id,
      });
    });
    store.dispatch(setGames(games));
  });
};

const loadGamePlayersAndSetGameActive = async (game: SqGameBackEndModel): Promise<void> => {
  const players: SqPlayer[] = await fetchGamePlayers(game.sqGameId);
  const sqGame: Maybe<SqGame> = updateActiveGameProps({ ...game, picks: [], players }, players);
  console.log(`${Emojis.eyes} updating game from loadGamePlayersAndSetGameActive`);
  console.dir(sqGame);

  if (sqGame) store.dispatch(setActiveSqGame(sqGame));
};

/**
 * Observable `GET` call for changes in the active game {@link SqGame}
 *
 * @description Function listens for the latest `snapshots` of
 * the active game, and updates the `redux` store. Every change
 * to game document will trigger this observer to update the
 * active game with the latest snapshot of the data.
 *
 * Note: We don not fetch active players here, because all changes to
 * the players are handled in {@link subscribeToPlayersInActiveGame}
 * function, with its own `Observer`. Here we only deal with
 * the changes to `SqGame` data directly.
 *
 * @param {String} gameId active game
 */
export const subscribeToGameUpdates = async (gameId: string): Promise<void> => {
  unsubscribeFromActiveGame = onSnapshot(doc(db, `games/${gameId}`).withConverter(gameConverter), (doc) => {
    if (doc.exists()) {
      const docData: SqGameBackEndModel = doc.data();

      const qsMetaFromCache = doc.metadata.fromCache;
      const qsMetaHasPendingWrites = doc.metadata.hasPendingWrites;
      const msgCache = `fromCache: %c ${qsMetaFromCache} %c`;
      const msgCacheCls = qsMetaFromCache ? ArmyGreenCls : CrimsonCls;
      const msgPendingWrites = `hasPendingWrites: %c ${qsMetaHasPendingWrites} %c`;
      const msgPendingWritesCls = qsMetaHasPendingWrites ? ArmyGreenCls : CrimsonCls;
      const msg = `${Emojis.eyes} %c Game Subscription %c  ${msgCache} ${msgPendingWrites}`;
      console.log(`${msg}`, LocationCls, '', msgCacheCls, '', msgPendingWritesCls, '');

      const activeGame: Maybe<SqGame> = getActiveSqGame(store.getState());
      if (activeGame) {
        console.log(`${Emojis.eyes} Updating active game`);
        const game = { ...activeGame, ...docData };
        store.dispatch(setActiveSqGame(game));
      } else {
        console.log(`${Emojis.eyes} Active game has not been loaded, setting active game`);
        loadGamePlayersAndSetGameActive(docData);
      }
    }
  });
};

/**
 * Observable GET call for players in the active game.
 *
 * @description On every change to players in the active game,
 * observer will be notified, and the current active game will
 * be updated with the latest data.
 *
 * @param {String} gameId active game
 */
export const subscribeToPlayersInActiveGame = async (gameId: string): Promise<void> => {
  const collectionData = collection(db, `games/${gameId}/players`).withConverter(playerConverter);
  const q = query(collectionData);

  unsubscribeFromPlayersInActiveGame = onSnapshot(q, (querySnapshot) => {
    let players: SqPlayer[] = [];
    querySnapshot.docs.forEach((doc) => {
      const docData: SqPlayer = doc.data();
      players = [...players, { ...docData }];
    });

    const qsMetaFromCache = querySnapshot.metadata.fromCache;
    const qsMetaHasPendingWrites = querySnapshot.metadata.hasPendingWrites;
    const msgCache = `fromCache: %c ${qsMetaFromCache} %c`;
    const msgCacheCls = qsMetaFromCache ? ArmyGreenCls : CrimsonCls;
    const msgPendingWrites = `hasPendingWrites: %c ${qsMetaHasPendingWrites} %c`;
    const msgPendingWritesCls = qsMetaHasPendingWrites ? ArmyGreenCls : CrimsonCls;
    const msg = `${Emojis.eyes} %c Players Subscription %c  ${msgCache} ${msgPendingWrites}`;
    console.log(`${msg}`, LocationCls, '', msgCacheCls, '', msgPendingWritesCls, '');

    /**
     * When entering the game from the `Lobby` games list should exist and we can get the active game from that list,
     * however when refreshing the active game, we lose the games list because we are already in the active game,
     * so we need to get the active game another way.
     */
    const gameId = players.length > 0 ? players[0].gameId : '';
    const games = getAllSqGames(store.getState());
    let activeGame: Maybe<SqGameBackEndModel> = games.find((game) => game.sqGameId === gameId);
    if (!activeGame) {
      activeGame = store.getState().activegame;
    }

    if (activeGame && activeGame.sqGameId !== '') {
      const sqGame = updateActiveGameProps({ ...activeGame, picks: [], players: [] }, players);
      console.log(`${Emojis.eyes} updating game from player subscription`);
      console.dir(sqGame);
      // if (sqGame) store.dispatch(setActiveSqGame(sqGame));
    } else {
      console.log(`${Emojis.warning} %c NO ACTIVE GAME %c found in the store`, CrimsonCls, '');
    }
  });
};

const getGame = async (gameId: string): Promise<Maybe<SqGame>> => {
  const docRef = doc(db, `games/${gameId}`).withConverter(gameConverter);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    const docData: SqGameBackEndModel = docSnap.data();
    const players: SqPlayer[] = await fetchGamePlayers(gameId);
    const sqGame = updateActiveGameProps({ ...docData, picks: [], players: [] }, players);
    console.log(`${Emojis.eyes} updating game from %c getGame %c`, LocationCls, '');
    console.dir(sqGame);
    return sqGame;

    // const playerIds = players.map((p) => p.id);
    // const openSquares = 100 - players.reduce((sum, p) => sum + p.numberOfPicks, 0);
    // const picks = generatePicksList(players);
    // const sqGame = { ...docData, sqGameId: docData.sqGameId, picks, players, playerIds, openSquares };
    // return sqGame;
  } else {
    const msg = `${Emojis.eyes} No such document! %c ${gameId} %c`;
    console.log(msg, ValueCls, '');
    return undefined;
  }
};

export const banPlayerFromGameInFB = async (playerId: SqUserID, game: SqGame): Promise<void> => {
  const playerRef = doc(db, `games/${game.sqGameId}/players/${playerId}`);
  await deleteDoc(playerRef);
  await postGame(game);
  return;
};

export const markPlayerAsBannedInFB = async (playerId: SqUserID, game: SqGame): Promise<void> => {
  const playerRef = doc(db, `games/${game.sqGameId}/players/${playerId}`);
  await deleteDoc(playerRef);
  await postGame(game);
  return;
};

/**
 * Single GET call for players in an active game.
 *
 * @param {string} gameId id of the game from where to fetch the players
 * @returns {Promise<SqPlayer[]>} return `Promise` containing a list of players `SqPlayer[]` from `gameId` game
 */
export const fetchGamePlayers = async (gameId: string): Promise<SqPlayer[]> => {
  const collectionData = collection(db, `games/${gameId}/players`).withConverter(playerConverter);
  const q = query(collectionData);

  const querySnapshot = await getDocs(q);
  const players: SqPlayer[] = [];
  querySnapshot.docs.forEach((doc) => {
    const docData: SqPlayer = doc.data();
    players.push({
      ...docData,
    });
  });

  return players;
};
// #endregion

// #region TEMP all getters/setters TODO: Organize
const postGame = async (game: SqGame): Promise<SqGame> => {
  const gameRef = doc(db, `games/${game.sqGameId}`);
  const docGame = gameToDoc(game);
  await updateDoc(gameRef, docGame);
  return game;
};

export const fetchGameFromFB = async (
  gameId: string,
  callbackFn?: (game: SqGame) => void
): Promise<Maybe<SqGame | undefined>> => {
  const game = await getGame(gameId);
  if (game && callbackFn) callbackFn(game);
  return game;
};

// export const playerLeaveGame = async (game: SqGame, player: SqPlayer): ReturnGame => {
//   await removePlayerFromGameInFB(player);
//   const gamePlayers = game.players.slice();
//   const index = gamePlayers.findIndex((p) => p.id === player.id);
//   gamePlayers.splice(index, 1);

//   const gamePicks = game.picks.slice();
//   player.picks.forEach((p) => (gamePicks[p] = ''));

//   const pickingPlayer = game.pickingPlayer === player.id ? '' : game.pickingPlayer;

//   const updatedGame: SqGame = { ...game, pickingPlayer, picks: gamePicks, players: gamePlayers };
//   const savedGame = await postGame(updatedGame);
//   const msg = `${Emojis.eyes} Removed player %c ${player.name} %c from game %c ${savedGame?.title} %c`;
//   console.log(msg, ValueCls, '', ValueCls, '');
//   return savedGame;
// };

export const playerJoinGame = async (
  newPlayer: SqPlayer,
  callbackFn: (g: SqGame, p: SqPlayer) => void
): Promise<Maybe<SqGame>> => {
  const player = await addPlayerToGameInFB(newPlayer);
  const game: Maybe<SqGame> = await fetchGameFromFB(newPlayer.gameId);

  if (game) {
    const msg = `${Emojis.eyes} Added player %c ${player.name} %c to game %c ${game.title} %c`;
    console.log(msg, ValueCls, '', ValueCls, '');
    postGame(game);
    callbackFn(game, player);
  }
  return game;
};

export type ReturnCreatedGame = ReturnType<typeof createGameInFB>;
export type ReturnGame = Promise<Maybe<SqGame>>;
export type CallbackWithGame = (game: SqGame) => void;

export const createGameInFB = async (game: SqGame, player: SqPlayer): Promise<SqGame> => {
  const collectionData = collection(db, 'games').withConverter(gameConverter);
  const docRef = doc(collectionData);
  const gameId = docRef.id;

  const playerWithGameId: SqPlayer = { ...player, gameId, isAdmin: true, createdOn: Date.now() };
  const playerRefData = collection(db, `games/${playerWithGameId.gameId}/players`).withConverter(playerConverter);
  const playerDocRef = doc(playerRefData, playerWithGameId.id);

  // await setDoc(docRef, player);
  const adminPlayer: SqPlayer = { ...playerWithGameId }; //await addPlayerToGameInFB(playerWithGameId);
  const gameAdmin = { id: adminPlayer.id, name: adminPlayer.name };
  const playerIds = [adminPlayer.id];
  const players = [adminPlayer];
  const url = getGameUrl(gameId);
  // Create new record with autogenerated gameId as SqGameId
  const gameToBeCreated: SqGame = { ...game, sqGameId: gameId, gameAdmin, playerIds, players, url };
  // const convertedGame = gameToDoc(gameToBeCreated);
  // await setDoc(docRef, convertedGame);

  const batch = writeBatch(db);
  batch.set(docRef, gameToDoc(gameToBeCreated));
  batch.set(playerDocRef, playerToDoc(adminPlayer));
  batch.commit();

  const msg = `${Emojis.eyes} Game Created, %c ${gameToBeCreated.title} %c %c ${gameId} %c`;
  console.log(msg, ValueCls, '', ValueCls, '');
  return gameToBeCreated as SqGame;
};

export const updateGamePlay = async (game: SqGame): Promise<void> => {
  // const sqGame = updateActiveGameProps(game, game.players);
  await postGame(game);
};

export const updateBannedPlayerGame = async (game: SqGame, bannedPlayerId: SqUserID): Promise<void> => {
  const batch = writeBatch(db);
  const gameRef = doc(db, `games/${game.sqGameId}`);
  const gamePlayersRef = doc(db, `games/${game.sqGameId}/players/${bannedPlayerId}`);

  console.log(`${Emojis.eyes} %c BANNING %c player %c ${bannedPlayerId} %c`, ActionCls, '', ValueCls, '');

  batch.update(gameRef, {
    playerIds: arrayRemove(bannedPlayerId),
    bannedPlayerIds: arrayUnion(bannedPlayerId),
    boardLocked: game.boardLocked,
    openSquares: game.openSquares,
    gameState: game.gameState,
    gameProgress: game.gameProgress,
  });
  batch.delete(gamePlayersRef);

  return await batch.commit();
};

export const updatePlayerNumberOfPicksInFB = async (
  player: SqPlayer,
  numberOfPicks: number,
  openSquares: number
): Promise<void> => {
  const playerRef = doc(db, `games/${player.gameId}/players/${player.id}`);
  const gameRef = doc(db, `games/${player.gameId}`);
  const batch = writeBatch(db);
  batch.update(playerRef, { numberOfPicks });
  batch.update(gameRef, { openSquares });
  return await batch.commit();
};

export const updateMaxPicksPerPlayerInFB = async (gameId: SqGameID, maxPicksPerPlayer: number): Promise<void> => {
  const gameRef = doc(db, `games/${gameId}`);
  await updateDoc(gameRef, { maxPicksPerPlayer: maxPicksPerPlayer });
};

export const updateStartingGameDateInFB = async (gameId: SqGameID, startingDate: number): Promise<void> => {
  const gameRef = doc(db, `games/${gameId}`);
  return updateDoc(gameRef, { startingDate });
};

export const updatePlayerPickInFB = async (player: SqPlayer, pick: number): Promise<void> => {
  const playerRef = doc(db, `games/${player.gameId}/players/${player.id}`);
  await updateDoc(playerRef, { picks: arrayUnion(pick) });
};

export const updatePlayerMultiplePicksInFB = async (player: SqPlayer, picks: number[]): Promise<void> => {
  const playerRef = doc(db, `games/${player.gameId}/players/${player.id}`);
  await updateDoc(playerRef, { picks: arrayUnion(...picks) });
};

export const removePlayerPickInFB = async (player: SqPlayer, pick: number): Promise<void> => {
  const playerRef = doc(db, `games/${player.gameId}/players/${player.id}`);
  await updateDoc(playerRef, { picks: arrayRemove(pick) });
};

export const updatePlayerNameInFB = async (player: SqPlayer, name: string): Promise<void> => {
  const playerRef = doc(db, `games/${player.gameId}/players/${player.id}`);
  await updateDoc(playerRef, { name });
};

export const updatePlayerMarkerInFB = async (player: SqPlayer, marker: SqPlayerMarker): Promise<void> => {
  const playerRef = doc(db, `games/${player.gameId}/players/${player.id}`);
  await updateDoc(playerRef, { marker });
};

// export const updatePlayerWithGameRefresh = async (player: SqPlayer, successCb?: CallbackWithGame): ReturnGame => {
//   await updatePlayerInGameInFB(player);
//   const game = await getGame(player.gameId);
//   if (successCb && game) successCb(game);
//   return game;
// };

// export const removePlayerWithGameRefresh = async (player: SqPlayer, successCb?: CallbackWithGame): ReturnGame => {
//   await removePlayerFromGameInFB(player);
//   const game = await getGame(player.gameId);
//   if (successCb && game) successCb(game);
//   return game;
// };

export const updatePlayerInGameInFB = async (player: SqPlayer): Promise<void> => {
  const playerRef = doc(db, `games/${player.gameId}/players/${player.id}`);
  console.log(`${Emojis.eyes} Update Player %c winning qtrs[${player.winningQuarters.toString()}] %c`, ValueCls, '');
  const docPlayer = playerToDoc(player);
  return await updateDoc(playerRef, docPlayer);
};

export const addPlayerToGameInFB = async (player: SqPlayer): Promise<SqPlayer> => {
  const collectionData = collection(db, `games/${player.gameId}/players`).withConverter(playerConverter);
  const docRef = doc(collectionData, player.id);

  await setDoc(docRef, player);
  return player;
};

// export const removePlayerFromGameInFB = async (player: SqPlayer): Promise<void> => {
//   const collectionData = collection(db, `games/${player.gameId}/players`).withConverter(playerConverter);
//   const docRef = doc(collectionData, player.id);

//   return await deleteDoc(docRef);
// };
// #endregion

export type ResponseWithGame = {
  game: SqGame;
  status: Promise<boolean>;
};

export type GameLoaderType = Promise<ResponseWithGame | Response>;

// #region LOADERS ************************************************************
export const loadGameByIdLoader = async ({ params }: LoaderFunctionArgs): GameLoaderType => {
  const gameId = params.gameId;
  if (!gameId) {
    console.log(`${Emojis.warning} No gameId provided`);
    return redirect(SqRoutes.Lobby);
  }

  console.log(`${Emojis.eyes} Loading %c gameId: ${gameId} %c`, ValueCls, '');

  const status = waitForUserToLogIn();
  const game = await fetchGameFromFB(gameId);
  console.log(`${Emojis.eyes} user logged in status ${status}`);

  if (game && auth.currentUser) {
    // store.dispatch(doLoadExistingGame(game));
    store.dispatch(setActiveSqGameId(gameId));
    store.dispatch(setActiveSqPlayerId(auth.currentUser.uid));
    store.dispatch(setActiveSqGame(game));
    return { game, status };
  } else {
    console.log(`${Emojis.warning} Unable to load the game %c ${gameId} %c`, CrimsonCls, '');
    return redirect(SqRoutes.Lobby);
  }
};

// #region ACTIONS ************************************************************
export const createJoinResumeGameAction = async ({ request }: ActionFunctionArgs): Promise<Response> => {
  console.log(`${Emojis.eyes} Called Action %c createJoinResumeGameAction %c`, ArmyGreenCls, '');
  const data = await request.formData();
  const payloadData = data.get('payload');
  if (!payloadData) {
    console.log(`${Emojis.warning} No payload provided`);
    return redirect(SqRoutes.Lobby);
  }

  const payload: SqGamePayloadAction = JSON.parse(payloadData as string);
  const { redirectUrl, gameId, type, playerId, gameName, matchId } = payload;
  const user: FbSqUser = getFbSqUser(store.getState()) as FbSqUser;

  let updatedGameId = gameId;
  let updatedRedirectUrl = redirectUrl;

  if (type === SqGamePayloadActionType.CreateGame) {
    // create game id
    const initSqPlayer = getInitialSqPlayer(user, true);
    const msg1 = `%c ${'CREATED'} %c Player`;
    // add player to the players collection of the game
    // create player with that game id
    // complete game creations with newly created player
    let sqGameToCreate = initialSquareGame(gameName, user, matchId);
    if (matchId) {
      const enhancedMatches = getEnhancedMatches(store.getState());
      if (enhancedMatches) {
        const match = enhancedMatches.find((m) => m.matchId === matchId);
        if (match) {
          const hTeam = match.home!.team;
          const homeTeam: SqTeam = {
            ...sqGameToCreate.homeTeam,
            id: hTeam.teamID,
            logoUrl: hTeam.logoLgUrl,
            name: hTeam.teamAbv,
          };
          const aTeam = match.away!.team;
          const awayTeam: SqTeam = {
            ...sqGameToCreate.awayTeam,
            id: aTeam.teamID,
            logoUrl: aTeam.logoLgUrl,
            name: aTeam.teamAbv,
          };
          sqGameToCreate = { ...sqGameToCreate, homeTeam, awayTeam };
        }
      }
    }

    const game: SqGame = await createGameInFB(sqGameToCreate, initSqPlayer);
    const msg2 = `%c ${'ADDED'} %c Player to Game %c COLLECTION %c`;
    const msg3 = `%c ${'CREATED'} %c Game`;
    // add game to list
    // store.dispatch(addGame(game));
    const info = `${Emojis.eyes} ${msg1} | ${msg2} | ${msg3}`;
    console.log(`${info}`, ArmyGreenCls, '', ArmyGreenCls, '', LocationCls, '', ArmyGreenCls, '');
    updatedGameId = game.sqGameId;
    updatedRedirectUrl = SqRoutes.getGameRoute(game.sqGameId);
  } else if (type === SqGamePayloadActionType.JoinGame) {
    console.log(`${Emojis.voiceAmp} %c JOIN GAME %c Action`, InfoCls, '');
    // create game player model
    const gm: Maybe<SqGameBackEndModel> = getAllSqGames(store.getState()).find((g) => g.sqGameId === gameId);
    const markerIndex = gm ? gm.playerIds.length : 0;
    const initPlayer = getInitialSqPlayer(user, false, 0, markerIndex, updatedGameId);
    const msg1 = `%c ${'CREATED'} %c Player`;
    // add player to the players collection of the game
    const batch = writeBatch(db);
    const playerRef = doc(db, `games/${gameId}/players/${playerId}`);
    const gameRef = doc(db, `games/${gameId}`);
    batch.set(playerRef, playerToDoc(initPlayer));
    batch.update(gameRef, { playerIds: arrayUnion(playerId) });
    await batch.commit();
    // const player = await addPlayerToGameInFB({ ...initPlayer, gameId });
    const msg2 = `%c ${'ADDED'} %c Player to Game %c COLLECTION %c`;
    // add player to the game playerIds list
    // await updateDoc(gameRef, { playerIds: arrayUnion(player.id) });
    const msg3 = `%c ${'ADDED'} %c Player to Game %c PlayerIds %c`;
    const info = `${Emojis.eyes} ${msg1} | ${msg2} | ${msg3}`;
    console.log(`${info}`, ArmyGreenCls, '', ArmyGreenCls, '', LocationCls, '', ArmyGreenCls, '', LocationCls, '');
  } else {
    // if (type === SqGamePayloadActionType.ResumeGame) {
    // resume game does not require anything special to be done, ony generic actions
  }

  // don't load game BUT set game as active, set player as active, redirect
  store.dispatch(setActiveSqGameId(updatedGameId));
  store.dispatch(setActiveSqPlayerId(playerId));
  return redirect(updatedRedirectUrl);
};
